import { Alert, Box, Button, Skeleton, Snackbar, Stack } from '@mui/material';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { extractErrorMessage, extractJobErrorMessage, resources } from '../../core/services';
import { updateViewContext } from '../../core/services/viewContext';
import { usePermissions } from '../../shared/hooks/usePermissions';
import { useActiveProject } from '../../core/contexts/ProjectContext';
import { NewProjectDialog } from '../../shared/components/NewProjectDialog';
import { isWizardOnlyProject } from '../onboarding/projectTypeProfiles';
import { EntityDetailsDialog } from '../entities/EntityDetailsDialog';
import { MainEntitiesSection } from '../entities/project-dashboard/MainEntitiesSection';
import { KeyEntitiesRadarRow } from '../entities/project-dashboard/KeyEntitiesRadarRow';
import { useMainEntitiesConfig } from '../entities/project-dashboard/useMainEntitiesConfig';
import { ActiveJobsBlock } from './dashboard/ActiveJobsBlock';
import { EmptyProjectGenerator } from './dashboard/EmptyProjectGenerator';
import { GenerationProgressPanel } from './dashboard/GenerationProgressPanel';
import { HighLevelOverviewSection } from './dashboard/HighLevelOverviewSection';
import { ProjectHero } from './dashboard/ProjectHero';
import { VisualViewsSection } from './dashboard/VisualViewsSection';
import { JOB_TYPE_LABEL } from './dashboard/jobDisplay';
import { useDashboardCardTabs } from './dashboard/useDashboardCardTabs';
import { DocumentQuestionsDialog } from './dialogs/DocumentQuestionsDialog';
import { ProjectPromptDialog } from './dialogs/ProjectPromptDialog';
import { VisualViewDialog } from './dialogs/VisualViewDialog';
import type {
  DocumentQuestionAnswer,
  DocumentVisualization,
  Entity,
  GenerateQuestionsJobResult,
  Job,
} from '../../core/types/data-models';

/** DocumentType.label the empty-project "Generate product spec" flow generates. */
const EMPTY_PROJECT_DOC_TYPE_LABEL = 'PRODUCT_ANALYSIS';

/**
 * /projects/:id — the project dashboard. Owns the project's documents and
 * job state (the generate flow, clarifying questions, failures, active jobs)
 * and lays out the dashboard blocks under ./dashboard and the entity blocks
 * under ../entities/project-dashboard, each fed its slice from here.
 */
export function ProjectDetail() {
  // Keyed by id so switching projects remounts the dashboard. React Router
  // otherwise reuses the instance across /projects/:id changes, carrying the
  // previous project's generate job, story prompt and dismissals over.
  const { id } = useParams<{ id: string }>();
  // Same key as the dashboard's own query, so the two share one cache entry.
  const projectQuery = useQuery({
    queryKey: ['projects', Number(id)],
    queryFn: () => resources.projects.get(Number(id)),
    enabled: Number.isFinite(Number(id)),
  });
  // An idea validation has no overview: its project opens on the wizard.
  if (isWizardOnlyProject(projectQuery.data?.projectType)) {
    return <Navigate to={`/projects/${id}/validation`} replace />;
  }
  return <ProjectDashboard key={id} />;
}

function ProjectDashboard() {
  const params = useParams<{ id: string }>();
  const projectId = Number(params.id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeProject, setActiveProject } = useActiveProject();
  const { canEdit } = usePermissions();
  const [editOpen, setEditOpen] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  // The empty-project generator's story prompt — owned here so it survives a
  // failed generation attempt (the generator unmounts while the job runs).
  const [storyPrompt, setStoryPrompt] = useState('');

  const [generating, setGenerating] = useState<'idle' | 'submitting' | 'polling'>('idle');
  const [activeJobId, setActiveJobId] = useState<number | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);

  // Visual Views carousel: the visualization currently open in the full-size dialog.
  const [selectedViz, setSelectedViz] = useState<DocumentVisualization | null>(null);

  // Main Entities: the entity currently open in the Entity Details dialog.
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);

  // Clarifying-questions flow: the questions job's own record on the server
  // (unresolvedQuestionsJob, derived below from historyQuery) is the single
  // source of truth for "is there a set of questions waiting on an answer" —
  // not local state — so it's rediscoverable after navigating away and back,
  // or after a full page reload. Dismissing the dialog only hides it for the
  // current mount; `questionsDialogDismissed` resets whenever the underlying
  // job changes (answered, expired, or a new one starts).
  const [questionsDialogDismissed, setQuestionsDialogDismissed] = useState(false);
  const [questionsSubmitting, setQuestionsSubmitting] = useState(false);
  const [questionsError, setQuestionsError] = useState<string | null>(null);

  // Retrying a failed job, and a snackbar for an attempt blocked by the
  // one-job-per-project guard (see projectBusyMessage below).
  const [retrySubmitting, setRetrySubmitting] = useState(false);
  const [blockedNotice, setBlockedNotice] = useState<string | null>(null);

  // Jobs the user has explicitly closed out of the failure/expiry banners.
  // Kept client-side (per project, in localStorage) — a dismissed failure
  // doesn't need a durable audit trail, just to stop reappearing on reload.
  const dismissedJobsStorageKey = `vibelin.dismissedJobs.${projectId}`;
  const [dismissedJobIds, setDismissedJobIds] = useState<Set<number>>(() => {
    try {
      const raw = localStorage.getItem(dismissedJobsStorageKey);
      return raw ? new Set(JSON.parse(raw) as number[]) : new Set();
    } catch {
      return new Set();
    }
  });
  const dismissJob = (jobId: number) => {
    setDismissedJobIds((prev) => {
      const next = new Set(prev).add(jobId);
      try {
        localStorage.setItem(dismissedJobsStorageKey, JSON.stringify([...next]));
      } catch {
        // best-effort — losing the dismissal just means the banner reappears next reload
      }
      return next;
    });
  };

  // Cancelling a job that's been running past JOB_STALL_MS (see ActiveJobsBlock).
  const [cancellingJobId, setCancellingJobId] = useState<number | null>(null);
  const handleCancelJob = async (job: Job) => {
    setCancellingJobId(job.id);
    try {
      await resources.cancelJob(job.id);
      if (activeJobId === job.id) {
        setActiveJobId(null);
        setGenerating('idle');
      }
      queryClient.invalidateQueries({ queryKey: ['project-jobs-active', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-jobs-history', projectId] });
    } catch (e) {
      setGenerateError(extractErrorMessage(e));
    } finally {
      setCancellingJobId(null);
    }
  };

  const projectQuery = useQuery({
    queryKey: ['projects', projectId],
    queryFn: () => resources.projects.get(projectId),
    enabled: Number.isFinite(projectId),
  });

  // A project created by the onboarding wizard with "Create only" carries its
  // story in userPrompt — start the editor from it rather than a blank page.
  useEffect(() => {
    if (!storyPrompt && projectQuery.data?.userPrompt) setStoryPrompt(projectQuery.data.userPrompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectQuery.data?.userPrompt]);

  const docsQuery = useQuery({
    queryKey: ['documents', { projectId }],
    queryFn: () =>
      resources.documents.list({
        pageSize: 100,
        filter: { projectId },
        sort: '-updatedAt',
      }),
    enabled: Number.isFinite(projectId),
  });

  // Polls the single job submitted from this session's GENERATE button
  const jobPollQuery = useQuery({
    queryKey: ['bg-job', activeJobId],
    queryFn: () => resources.getJob(activeJobId!),
    enabled: generating === 'polling' && activeJobId != null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'COMPLETED' || status === 'FAILED' || status === 'CANCELLED') return false;
      return 2000;
    },
  });

  useEffect(() => {
    const job = jobPollQuery.data;
    if (!job) return;
    if (job.status === 'COMPLETED') {
      setGenerating('idle');
      setActiveJobId(null);
      docsQuery.refetch();
      queryClient.invalidateQueries({ queryKey: ['sidebar-documents'] });
      queryClient.invalidateQueries({ queryKey: ['project-entity-types', projectId] });
      queryClient.invalidateQueries({ queryKey: ['document-section-entities', projectId] });
    } else if (job.status === 'FAILED' || job.status === 'CANCELLED') {
      setGenerating('idle');
      setActiveJobId(null);
      setGenerateError(job.error ?? `Job ${job.status.toLowerCase()}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPollQuery.data]);

  // Polls only the project's running jobs (server-side status filter) for the
  // "In Progress" block — this is the one that needs a tight 3s cadence, so
  // keeping it scoped to PENDING/PROCESSING keeps each poll cheap regardless
  // of how much job history the project has accumulated.
  const activeJobsQuery = useQuery({
    queryKey: ['project-jobs-active', projectId],
    queryFn: () =>
      resources.jobs.list({
        filter: { projectId: [projectId], status: ['PENDING', 'PROCESSING'] },
        pageSize: 20,
        sort: '-id',
      }),
    enabled: Number.isFinite(projectId),
    refetchInterval: (query) => ((query.state.data?.data?.length ?? 0) > 0 ? 3000 : false),
  });

  // Separately, enough recent job history (any status) to tell whether the
  // project is waiting on unanswered questions or has a recent failure —
  // read from the job rows themselves (status, resolvedByJobId), not local
  // state, so they're rediscoverable after navigating away and back or a
  // full page reload. Unlike activeJobsQuery this doesn't need to free-run on
  // a timer: it's refetched below whenever a job that was active finishes
  // (transitions out of PENDING/PROCESSING), plus after the mutations that
  // create or resolve jobs.
  const historyQuery = useQuery({
    queryKey: ['project-jobs-history', projectId],
    queryFn: () =>
      resources.jobs.list({
        filter: { projectId: [projectId] },
        pageSize: 20,
        sort: '-id',
      }),
    enabled: Number.isFinite(projectId),
  });

  // Detects jobs dropping out of the active set (completed/failed/cancelled)
  // to refresh history — that's what picks up a GENERATE_DOCUMENT_QUESTIONS
  // job going COMPLETED (opens the questions dialog) or a job FAILED. When
  // the active set drains to zero entirely, also refresh the docs list — this
  // is also what catches the auto-chained EXTRACT_DOCUMENT_ENTITIES and
  // GENERATE_DOCUMENT_METADATA jobs finishing — they run as their own job
  // rows, not ones we were tracking directly — so the entity-types dropdown
  // and chips pick up whatever new types/entities were just extracted without
  // a manual reload.
  const prevActiveJobIds = useRef<Set<number>>(new Set());
  useEffect(() => {
    const jobs = activeJobsQuery.data?.data ?? [];
    const ids = new Set(jobs.map((j) => j.id));
    const finished = [...prevActiveJobIds.current].some((id) => !ids.has(id));
    if (finished) {
      historyQuery.refetch();
    }
    if (prevActiveJobIds.current.size > 0 && ids.size === 0) {
      docsQuery.refetch();
      queryClient.invalidateQueries({ queryKey: ['sidebar-documents'] });
      queryClient.invalidateQueries({ queryKey: ['project-entity-types', projectId] });
      queryClient.invalidateQueries({ queryKey: ['document-section-entities', projectId] });
    }
    prevActiveJobIds.current = ids;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJobsQuery.data]);

  // Keep in sync with UNRESOLVED_QUESTIONS_EXPIRY_MS in the backend's
  // BgJobService.assertProjectAvailable — Decision 3 of the job UX design.
  const QUESTIONS_EXPIRY_MS = 60 * 60 * 1000;
  // Job types whose failure is worth interrupting the user about. A failed
  // GENERATE_DOCUMENT_METADATA never blocks or hides the document it summarizes
  // (it's a best-effort last step), so surfacing it as an error here would be
  // misleading — the generation it was attached to already succeeded.
  const FAILURE_VISIBLE_JOB_TYPES = new Set(['GENERATE_DOCUMENT_QUESTIONS', 'GENERATE_DOCUMENT']);

  function parseJobJson<T>(raw: string | null): T | null {
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  function isConflictError(err: unknown): boolean {
    return axios.isAxiosError(err) && err.response?.status === 409;
  }

  const recentJobs = historyQuery.data?.data ?? [];
  const activeJobs = activeJobsQuery.data?.data ?? [];

  // The most recent completed questions job that hasn't been resolved into a
  // generate job yet (resolvedByJobId is stamped by the backend once the user
  // answers, or immediately when zero questions were needed). Reappears
  // correctly after navigating away and back because it's read from the job
  // row itself, not from state that only existed in the mount that created it.
  const unresolvedQuestionsJob =
    recentJobs.find((j) => {
      if (j.jobType !== 'GENERATE_DOCUMENT_QUESTIONS' || j.status !== 'COMPLETED' || j.resolvedByJobId != null) {
        return false;
      }
      if (Date.now() - new Date(j.updatedAt).getTime() >= QUESTIONS_EXPIRY_MS) return false;
      const questions = parseJobJson<GenerateQuestionsJobResult>(j.result)?.questions ?? [];
      return questions.length > 0;
    }) ?? null;

  // Same shape, but past the expiry threshold — Decision 3: stops blocking new
  // generation, but still worth telling the user why their questions vanished.
  const expiredQuestionsJob =
    recentJobs.find((j) => {
      if (j.jobType !== 'GENERATE_DOCUMENT_QUESTIONS' || j.status !== 'COMPLETED' || j.resolvedByJobId != null) {
        return false;
      }
      if (dismissedJobIds.has(j.id)) return false;
      if (Date.now() - new Date(j.updatedAt).getTime() < QUESTIONS_EXPIRY_MS) return false;
      const questions = parseJobJson<GenerateQuestionsJobResult>(j.result)?.questions ?? [];
      return questions.length > 0;
    }) ?? null;

  const failedJob =
    recentJobs.find(
      (j) => j.status === 'FAILED' && FAILURE_VISIBLE_JOB_TYPES.has(j.jobType) && !dismissedJobIds.has(j.id),
    ) ?? null;

  const isProjectBusy = activeJobs.length > 0 || unresolvedQuestionsJob != null;

  function projectBusyMessage(): string {
    if (activeJobs.length > 0) {
      const label = JOB_TYPE_LABEL[activeJobs[0].jobType] ?? activeJobs[0].jobType.replace(/_/g, ' ');
      return `Can't start — ${label} is still running for this project. Try again once it finishes.`;
    }
    return 'This project has unanswered clarifying questions. Answer them before starting another job.';
  }

  // Auto-opens the questions dialog whenever an unresolved questions job is
  // found — including right after a fresh mount — so the flow can no longer
  // get silently lost by navigating away before answering. Resets the
  // dismissal whenever the underlying job changes (answered/expired/new).
  useEffect(() => {
    setQuestionsDialogDismissed(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unresolvedQuestionsJob?.id]);
  const questionsDialogOpen = unresolvedQuestionsJob != null && !questionsDialogDismissed;
  const questionsJobData = unresolvedQuestionsJob
    ? parseJobJson<{ documentType: string; userPrompt: string; skipEntities?: boolean; skipDashboard?: boolean }>(unresolvedQuestionsJob.data)
    : null;
  const pendingQuestions = unresolvedQuestionsJob
    ? (parseJobJson<GenerateQuestionsJobResult>(unresolvedQuestionsJob.result)?.questions ?? [])
    : [];

  const handleGenerate = async () => {
    if (!storyPrompt.trim()) return;
    if (isProjectBusy) {
      setBlockedNotice(projectBusyMessage());
      return;
    }
    // The document-type catalog is admin-managed, so resolve the type to
    // generate from its label rather than assuming label === type.
    const docType = (documentTypesQuery.data?.data ?? []).find(
      (dt) => dt.label === EMPTY_PROJECT_DOC_TYPE_LABEL,
    );
    if (!docType) {
      setGenerateError(`Document type "${EMPTY_PROJECT_DOC_TYPE_LABEL}" is not available.`);
      return;
    }
    setGenerating('submitting');
    setGenerateError(null);
    try {
      const { jobId } = await resources.generateDocument(projectId, {
        documentType: docType.type,
        generateUserPrompt: storyPrompt,
      });
      setActiveJobId(jobId);
      setGenerating('polling');
    } catch (e) {
      setGenerating('idle');
      if (isConflictError(e)) {
        setBlockedNotice(extractJobErrorMessage(e));
      } else {
        setGenerateError(extractErrorMessage(e));
      }
    }
  };

  // Enqueue the generate-document job with the user's answers (empty = skipped
  // all), tagged with the questions job it resolves. Once the backend stamps
  // resolvedByJobId, unresolvedQuestionsJob goes null on the next refetch and
  // the dialog/banner close on their own — no local "clear the flow" step needed.
  const handleSubmitAnswers = async (answers: DocumentQuestionAnswer[]) => {
    if (!unresolvedQuestionsJob || !questionsJobData) return;
    setQuestionsSubmitting(true);
    setQuestionsError(null);
    try {
      await resources.generateDocument(projectId, {
        documentType: questionsJobData.documentType,
        generateUserPrompt: questionsJobData.userPrompt,
        questions: answers,
        sourceQuestionsJobId: unresolvedQuestionsJob.id,
        skipEntities: questionsJobData.skipEntities,
        skipDashboard: questionsJobData.skipDashboard,
      });
      activeJobsQuery.refetch();
      historyQuery.refetch();
    } catch (e) {
      setQuestionsError(extractJobErrorMessage(e));
    } finally {
      setQuestionsSubmitting(false);
    }
  };

  // Retries a failed job using the request it was originally created with
  // (read back from its own `data`), so the user doesn't have to redo the
  // questions step just because generation failed downstream. Safe to re-run:
  // GENERATE_DOCUMENT reuses the same hidden document row via
  // findOrCreateDocument's projectId+documentType lookup rather than
  // duplicating it.
  const handleRetryFailedJob = async (job: Job) => {
    setRetrySubmitting(true);
    try {
      if (job.jobType === 'GENERATE_DOCUMENT_QUESTIONS') {
        const data = parseJobJson<{ documentType: string; userPrompt: string; skipEntities?: boolean; skipDashboard?: boolean }>(
          job.data,
        );
        if (!data) throw new Error('Missing job data for retry');
        await resources.generateDocumentQuestions(projectId, {
          documentType: data.documentType,
          generateUserPrompt: data.userPrompt,
          skipEntities: data.skipEntities,
          skipDashboard: data.skipDashboard,
        });
      } else if (job.jobType === 'GENERATE_DOCUMENT') {
        const data = parseJobJson<{
          documentType: string;
          userPrompt: string;
          questions?: DocumentQuestionAnswer[];
          skipEntities?: boolean;
          skipDashboard?: boolean;
        }>(job.data);
        if (!data) throw new Error('Missing job data for retry');
        await resources.generateDocument(projectId, {
          documentType: data.documentType,
          generateUserPrompt: data.userPrompt,
          questions: data.questions,
          skipEntities: data.skipEntities,
          skipDashboard: data.skipDashboard,
        });
      }
      dismissJob(job.id);
      activeJobsQuery.refetch();
      historyQuery.refetch();
    } catch (e) {
      setBlockedNotice(extractJobErrorMessage(e));
    } finally {
      setRetrySubmitting(false);
    }
  };

  const documentTypesQuery = useQuery({
    queryKey: ['documentTypes'],
    queryFn: () => resources.documentTypes.list({ pageSize: 500, sort: 'order' }),
  });

  // Main Entities and High Level Overview: each block's configured tabs, rows,
  // and the mutations that edit them.
  const mainEntities = useMainEntitiesConfig(projectId);
  const dashboardCards = useDashboardCardTabs(projectId);

  // Visual Views: every visualization generated across the project's documents.
  const visualizationsQuery = useQuery({
    queryKey: ['project-document-visualizations', projectId],
    queryFn: () =>
      resources.documentVisualizations.list({
        filter: { projectId: [projectId] },
        sort: '-createdAt',
        pageSize: 200,
      }),
    enabled: Number.isFinite(projectId),
  });

  // Sections referenced by those visualizations, fetched by id so the cards/dialog
  // can show the section's title and content without pulling every section's body.
  const visualizationSectionIds = Array.from(
    new Set(
      (visualizationsQuery.data?.data ?? [])
        .map((v) => v.documentSectionId)
        .filter((id): id is number => id != null),
    ),
  );
  const visualizationSectionsQuery = useQuery({
    queryKey: ['visualization-sections', projectId, visualizationSectionIds],
    queryFn: () =>
      resources.documentSections.list({
        filter: { id: visualizationSectionIds },
        pageSize: visualizationSectionIds.length,
      }),
    enabled: visualizationSectionIds.length > 0,
  });

  const project = projectQuery.data;

  // Let the AI Assistant show this project's name once it loads.
  useEffect(() => {
    if (project) updateViewContext({ projectId: project.id, projectName: project.name });
  }, [project]);

  // IN_GENERATION documents are still being written and aren't ready to show yet,
  // but they still count toward existingDocumentTypes below so a second generate
  // request for the same type isn't offered while one is already in flight.
  const visibleDocs = (docsQuery.data?.data ?? []).filter((d) => d.status !== 'IN_GENERATION');
  const docTypeMap = new Map(
    (documentTypesQuery.data?.data ?? []).map((dt) => [dt.type, dt]),
  );
  const docById = new Map((docsQuery.data?.data ?? []).map((d) => [d.id, d]));
  const sectionById = new Map((visualizationSectionsQuery.data?.data ?? []).map((s) => [s.id, s]));

  const hasDocuments = !docsQuery.isPending && visibleDocs.length > 0;
  const questionsDocType = questionsJobData ? (docTypeMap.get(questionsJobData.documentType) ?? null) : null;
  // What ActiveJobsBlock renders as chips: running jobs, plus the unresolved
  // questions job (if any) so it's clickable from there too, not just via the banner.
  const progressChipsJobs = unresolvedQuestionsJob ? [...activeJobs, unresolvedQuestionsJob] : activeJobs;
  const hasActiveJobs = progressChipsJobs.length > 0;
  const isContentLoading = docsQuery.isPending || activeJobsQuery.isPending || historyQuery.isPending;
  // True on the first-time "no documents yet" PRD generator screen — mirrors
  // the branch condition below that renders it, so the hero can hide actions
  // (ask box, prompt dialog, invite) that don't apply until a project exists.
  const isEmptyProjectFirstTime = !isContentLoading && generating === 'idle' && !hasDocuments && !hasActiveJobs;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 3, pb: 3 }}>
      <ProjectHero
        projectId={projectId}
        project={project}
        loading={projectQuery.isPending}
        compact={isEmptyProjectFirstTime}
        canEdit={canEdit}
        onOpenPrompt={() => setPromptOpen(true)}
        onEdit={() => setEditOpen(true)}
      />

      {/* ── Clarifying-questions banner (recoverable when the dialog is dismissed; survives navigating away and back) ── */}
      {unresolvedQuestionsJob && !questionsDialogOpen && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={() => setQuestionsDialogDismissed(false)}>
              Answer Questions
            </Button>
          }
        >
          {pendingQuestions.length} question{pendingQuestions.length === 1 ? '' : 's'} ready
          {questionsDocType ? ` for ${questionsDocType.name}` : ''}.
        </Alert>
      )}
      {questionsError && !questionsDialogOpen && (
        <Alert severity="error" onClose={() => setQuestionsError(null)}>
          {questionsError}
        </Alert>
      )}

      {/* ── Unanswered questions past the 1h threshold: no longer blocking, but worth explaining ── */}
      {expiredQuestionsJob && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => dismissJob(expiredQuestionsJob.id)}>
              Dismiss
            </Button>
          }
        >
          Questions expired — click Generate to start over.
        </Alert>
      )}

      {/* ── Most recent failed job: explain what broke, offer to retry or dismiss ── */}
      {failedJob && (
        <Alert
          severity="error"
          action={
            <Stack direction="row" spacing={1}>
              <Button
                color="inherit"
                size="small"
                disabled={retrySubmitting}
                onClick={() => handleRetryFailedJob(failedJob)}
              >
                Retry
              </Button>
              <Button color="inherit" size="small" onClick={() => dismissJob(failedJob.id)}>
                Dismiss
              </Button>
            </Stack>
          }
        >
          {JOB_TYPE_LABEL[failedJob.jobType] ?? failedJob.jobType} failed: {failedJob.error ?? 'Unknown error'}
        </Alert>
      )}

      {/* ── Blocked-attempt notice: buttons stay enabled, this explains why the click didn't start anything ── */}
      <Snackbar
        open={blockedNotice != null}
        autoHideDuration={5000}
        onClose={() => setBlockedNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setBlockedNotice(null)} sx={{ width: '100%' }}>
          {blockedNotice}
        </Alert>
      </Snackbar>

      {isContentLoading ? (
        /* ── Loading state ── */
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Skeleton variant="rectangular" sx={{ flex: 1, minWidth: 0, height: 220, borderRadius: 1 }} />
            <Skeleton variant="rectangular" sx={{ flex: 1, minWidth: 0, height: 220, borderRadius: 1 }} />
          </Stack>
          <Stack spacing={0.5}>
            {Array.from({ length: 3 }, (_, i) => <Skeleton key={i} height={40} />)}
          </Stack>
        </>
      ) : generating === 'polling' || generating === 'submitting' ? (
        /* ── Inline progress panel for job submitted this session ── */
        <GenerationProgressPanel job={jobPollQuery.data} />
      ) : (hasDocuments || hasActiveJobs) ? (
        /* ── Project has documents or active jobs: the dashboard ── */
        <>
          {hasActiveJobs && (
            <ActiveJobsBlock
              jobs={progressChipsJobs}
              docById={docById}
              docTypeMap={docTypeMap}
              onOpenQuestions={() => setQuestionsDialogDismissed(false)}
              onCancelJob={handleCancelJob}
              cancellingJobId={cancellingJobId}
            />
          )}

          <KeyEntitiesRadarRow projectId={projectId} activeJobs={activeJobs} onOpenEntity={setSelectedEntity} />

          {!dashboardCards.isPending && (
            <HighLevelOverviewSection
              tabs={dashboardCards.tabs}
              assignments={dashboardCards.assignments}
              cards={dashboardCards.cards}
              docById={docById}
              canEdit={canEdit}
              activeTabId={dashboardCards.activeTabId}
              onTabChange={dashboardCards.setActiveTabId}
              onAddCard={dashboardCards.addCard}
              onRemoveCard={dashboardCards.removeCard}
              onAddTab={dashboardCards.addTab}
              onDeleteTab={dashboardCards.deleteTab}
              onMoveCard={dashboardCards.moveCard}
              onResizeCard={dashboardCards.resizeCard}
            />
          )}

          {!mainEntities.isPending && (
            <MainEntitiesSection
              entityTypeRows={mainEntities.entityTypeRows}
              entityTabs={mainEntities.entityTabs}
              entities={mainEntities.entities}
              entitiesLoading={mainEntities.entitiesLoading}
              allEntityTypes={mainEntities.allEntityTypes}
              canEdit={canEdit}
              activeTabId={mainEntities.activeTabId}
              onTabChange={mainEntities.setActiveTabId}
              onAddType={mainEntities.addType}
              onDeleteType={mainEntities.deleteType}
              onAddTab={mainEntities.addTab}
              onDeleteTab={mainEntities.deleteTab}
              onOpenEntity={setSelectedEntity}
            />
          )}

          {!visualizationsQuery.isPending && (
            <VisualViewsSection
              visualizations={visualizationsQuery.data?.data ?? []}
              docById={docById}
              sectionById={sectionById}
              onOpen={setSelectedViz}
            />
          )}
        </>
      ) : (
        /* ── No documents: PRD generator ── */
        <EmptyProjectGenerator
          storyPrompt={storyPrompt}
          onStoryPromptChange={setStoryPrompt}
          error={generateError}
          onClearError={() => setGenerateError(null)}
          onGenerate={handleGenerate}
        />
      )}

      {project && (
        <ProjectPromptDialog
          open={promptOpen}
          projectName={project.name}
          prompt={project.generateUserPrompt}
          onClose={() => setPromptOpen(false)}
        />
      )}

      {project && (
        <NewProjectDialog
          open={editOpen}
          project={project}
          onClose={() => setEditOpen(false)}
          onCreated={(updated) => {
            if (activeProject?.id === updated.id) setActiveProject(updated);
          }}
          onDeleted={(deleted) => {
            if (activeProject?.id === deleted.id) setActiveProject(null);
            navigate('/');
          }}
        />
      )}

      <DocumentQuestionsDialog
        open={questionsDialogOpen}
        docTypeName={questionsDocType?.name ?? 'Document'}
        questions={pendingQuestions}
        submitting={questionsSubmitting}
        error={questionsError}
        onClose={() => setQuestionsDialogDismissed(true)}
        onSubmit={handleSubmitAnswers}
      />

      <VisualViewDialog
        viz={selectedViz}
        docName={selectedViz ? docById.get(selectedViz.documentId)?.name : undefined}
        section={selectedViz?.documentSectionId != null ? sectionById.get(selectedViz.documentSectionId) : undefined}
        onClose={() => setSelectedViz(null)}
      />

      <EntityDetailsDialog
        projectId={projectId}
        entity={selectedEntity}
        canEdit={canEdit}
        onClose={() => setSelectedEntity(null)}
      />
    </Box>
  );
}
